/**
 * Indicator Card Component - Displays a single chart/visualization.
 *
 * Routes the indicator's `chart_type` to the right component. The grid /
 * summary swap is dynamic — when a `summary` indicator carries explicit
 * column metadata in its config we render it as a grid; otherwise we fall
 * back to the topic summary cards.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { BarChart3, Maximize2, MoreVertical, Edit, Trash2, Settings2 } from 'lucide-react';
import { type DashboardIndicator } from '../../../lib/api';
import { CHART_TYPE_ICONS } from '../constants';
import { KPIIndicator } from './KPIIndicator';
import { BarChartComponent } from './BarChartComponent';
import { LineChartComponent } from './LineChartComponent';
import { PieChartComponent } from './PieChartComponent';
import { SummaryComponent } from './SummaryComponent';
import { GridComponent } from './GridComponent';
import { HeatmapComponent } from './HeatmapComponent';
import { WaterfallComponent } from './WaterfallComponent';
import { GeolocationComponent } from './GeolocationComponent';
import { IndicatorChartComponent } from './IndicatorChartComponent';
import { MarkUpComponent } from './MarkUpComponent';

interface IndicatorCardProps {
  indicator: DashboardIndicator;
  onEdit?: (indicator: DashboardIndicator) => void;
  onDelete?: (indicator: DashboardIndicator) => void;
  /** Opens the visual Report Builder for this indicator. */
  onCustomise?: (indicator: DashboardIndicator) => void;
}

export function IndicatorCard({ indicator, onEdit, onDelete, onCustomise }: IndicatorCardProps) {
  const [fullViewOpen, setFullViewOpen] = useState(false);

  const renderChart = (fullView = false) => {
    // In the full-view dialog we let charts expand to fill most of the
    // viewport so they don't look half-squashed.
    const height = fullView ? 560 : 250;
    const gridMax = fullView ? '60vh' : 320;
    switch (indicator.chart_type) {
      case 'kpi':
        return <KPIIndicator indicatorId={indicator.id} />;
      case 'indicator':
        return <IndicatorChartComponent indicatorId={indicator.id} />;
      case 'bar':
        return <BarChartComponent indicatorId={indicator.id} height={height} />;
      case 'waterfall':
        return <WaterfallComponent indicatorId={indicator.id} height={height} />;
      case 'line':
        return <LineChartComponent indicatorId={indicator.id} height={height} />;
      case 'pie':
        return <PieChartComponent indicatorId={indicator.id} height={height} />;
      case 'donut':
        return <PieChartComponent isDoughnut indicatorId={indicator.id} height={height} />;
      case 'grid':
        return <GridComponent indicatorId={indicator.id} maxHeight={gridMax} />;
      case 'summary': {
        // If the indicator carries grid columns in its config, prefer the
        // tabular view since it is strictly more informative.
        const cfg = (indicator.config || {}) as Record<string, unknown>;
        const cols = cfg['columns'];
        if (Array.isArray(cols) && cols.length > 0) {
          return <GridComponent indicatorId={indicator.id} maxHeight={gridMax} />;
        }
        return <SummaryComponent indicatorId={indicator.id} />;
      }
      case 'heatmap':
        return <HeatmapComponent indicatorId={indicator.id} />;
      case 'geolocation':
        return <GeolocationComponent indicatorId={indicator.id} />;
      case 'markup':
        return <MarkUpComponent indicatorId={indicator.id} />;
      default:
        return <BarChartComponent indicatorId={indicator.id} height={height} />;
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {CHART_TYPE_ICONS[indicator.chart_type] || <BarChart3 className="h-4 w-4" />}
            <CardTitle className="text-sm font-medium">{indicator.name}</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-xs">
              {indicator.chart_type_display || indicator.chart_type}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setFullViewOpen(true)}>
                    <Maximize2 className="h-4 w-4 mr-2" />
                    Full view
                  </DropdownMenuItem>
                  {onCustomise && (
                    <DropdownMenuItem onClick={() => onCustomise(indicator)}>
                      <Settings2 className="h-4 w-4 mr-2" />
                      Customise
                    </DropdownMenuItem>
                  )}
                  {onEdit && (
                    <DropdownMenuItem onClick={() => onEdit(indicator)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {onDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => onDelete(indicator)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {indicator.hint && (
          <CardDescription className="text-xs">{indicator.hint}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-0">{renderChart()}</CardContent>
      <Dialog open={fullViewOpen} onOpenChange={setFullViewOpen}>
        <DialogContent className="max-w-[90vw] sm:max-w-[1200px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {CHART_TYPE_ICONS[indicator.chart_type] || <BarChart3 className="h-4 w-4" />}
              {indicator.name}
              <Badge variant="outline" className="text-xs">
                {indicator.chart_type_display || indicator.chart_type}
              </Badge>
            </DialogTitle>
            {indicator.hint && (
              <CardDescription className="text-xs">{indicator.hint}</CardDescription>
            )}
          </DialogHeader>
          <div className="py-2">
            {renderChart(true)}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
